/**
 * 채점 산출식 — 노드 트리에서 대상 주소를 뽑고, 조건 트리를 Python 문자열로 만들고,
 * 저장된 문자열을 다시 사람 말로 읽는다.
 *
 * 저장되는 진실은 언제나 Python 문자열이다(df_evaluations.formula). 리포트가 그 문자열을
 * 그대로 읽으므로(report.supply.mixin.js:245 완전일치, ManagementLevelTable.vue:415 부분일치)
 * 계약을 바꾸지 않는다. 이 모듈은 그 문자열을 '만들고' '읽을' 뿐이다.
 *
 * staging 실측(2026-09-07, 3,365건): 기본 3패턴 62.6% + 중첩 17.3% + and/or 12.7% = 92.6%.
 * 나머지 7.4%(표 산술, any(), 깨진 것)는 읽기에서 '직접 작성' 으로 남긴다.
 */
import {CHOICE_PARENTS} from './structure-tree.js'

/* ══════════════════════════════════════════════════
 * 1. 대상: 답이 저장되는 자리
 *    각 대상은 자기 v1 주소(path)를 안다. 사람이 주소를 쓰지 않는다.
 * ══════════════════════════════════════════════════ */

const kindOfContent = (type) =>
    type === 'input_number' ? 'number'
    : type === 'dropdown' ? 'choice'
    : type === 'input_text' ? 'text'
    : type === 'file_URL2' ? 'file'
    : null

const choicesOf = (node) => (node.children || [])
    .filter((c) => c.type === 'plain_text')
    .map((c) => ({text: c.label, value: c.value !== undefined ? c.value : c.label}))

/**
 * 트리를 v1 인덱스 그대로 따라 내려가며 답이 저장되는 노드만 모은다.
 * kind: choice(라디오/드롭다운) · multi(체크박스) · number · text · file
 */
export const targetsOf = (root) => {
    const out = []
    if (!root) return out
    const push = (node, kind, path, say, extra = {}) => out.push({id: node.id, kind, path, say, ...extra})

    const table = (node, base, sayBase) => {
        const cols = node.columns || []
        ;(node.children || []).forEach((row, r) => {
            ;(row.children || []).forEach((cell, c) => {
                const content = cell.children && cell.children[0]
                const col = cols[c]
                if (!content || !col) return
                const kind = kindOfContent(content.type)
                if (!kind) return
                const path = `${base}['structure'][${r}]['${col.label}']`
                const say = `${sayBase}표 ${r + 1}행 「${col.label}」`
                push(content, kind, path, say, kind === 'choice' ? {choices: choicesOf(content)} : {})
            })
        })
    }

    const t = root.subType || root.type
    if (t === 'table') {
        // 루트가 표면 행이 곧 structure[r]
        const cols = root.columns || []
        ;(root.children || []).forEach((row, r) => {
            ;(row.children || []).forEach((cell, c) => {
                const content = cell.children && cell.children[0]
                const col = cols[c]
                if (!content || !col) return
                const kind = kindOfContent(content.type)
                if (!kind) return
                push(content, kind, `structure[${r}]['${col.label}']`, `표 ${r + 1}행 「${col.label}」`,
                    kind === 'choice' ? {choices: choicesOf(content)} : {})
            })
        })
        return out
    }
    if (CHOICE_PARENTS.includes(t)) {
        push(root, t === 'checkbox' ? 'multi' : 'choice', 'result', '답', {choices: choicesOf(root)})
        ;(root.children || []).forEach((choice, i) => {
            ;(choice.children || []).forEach((el, j) => {
                const base = `structure[${i}]['element_contents'][${j}]`
                const sayBase = `${i + 1}번 선택지 안 `
                if (el.type === 'table') table(el, base, sayBase)
                else if (CHOICE_PARENTS.includes(el.type)) {
                    push(el, el.type === 'checkbox' ? 'multi' : 'choice', `${base}['result']`, `${sayBase}선택`, {choices: choicesOf(el)})
                } else {
                    const kind = kindOfContent(el.type)
                    if (kind) push(el, kind, `${base}['result']`, `${sayBase}${kind === 'number' ? '숫자' : kind === 'file' ? '첨부' : '입력'}`)
                }
            })
        })
        return out
    }
    const kind = kindOfContent(t)
    if (kind) push(root, kind, 'result', '답')
    return out
}

/* ══════════════════════════════════════════════════
 * 2. 비교 연산 — 대상 종류마다 쓸 수 있는 것. 표기는 여기서 한 번만 정한다.
 *    (result=='값' 형태를 고정해야 리포트의 정규식/부분일치가 그대로 맞는다)
 * ══════════════════════════════════════════════════ */
const NUM_SAY = {'>=': '이상이면', '>': '초과이면', '<=': '이하이면', '<': '미만이면', '==': '이면'}

export const OPS = {
    choice: [
        {k: '==', say: '이면', py: (p, v) => `${p}=='${v}'`},
        {k: '!=', say: '이 아니면', py: (p, v) => `${p}!='${v}'`}
    ],
    multi: [
        {k: 'in', say: '를 골랐으면', py: (p, v) => `'${v}' in ${p}`},
        {k: 'not in', say: '를 안 골랐으면', py: (p, v) => `'${v}' not in ${p}`}
    ],
    number: ['>=', '>', '<=', '<', '=='].map((k) => ({k, say: NUM_SAY[k], py: (p, v) => `float(${p}) ${k} ${v}`})),
    text: [
        {k: '!=""', say: '이 비어 있지 않으면', py: (p) => `${p} != ''`},
        {k: '==""', say: '이 비어 있으면', py: (p) => `${p} == ''`}
    ],
    file: [
        {k: 'has', say: '이 있으면', py: (p) => `${p}[0]['filename'] !=''`}
    ]
}

/* ══════════════════════════════════════════════════
 * 3. 조건 트리 → Python
 *    group: {kind:'group', join:'and'|'or', items:[...]}  cond: {kind:'cond', target, op, value}
 *    한 그룹에는 접속사 하나. 섞으려면 그룹을 만든다 — Python 우선순위에 기대지 않는다.
 *    실데이터에서 and/or 를 섞은 27건은 전부 괄호로 묶여 있었다.
 * ══════════════════════════════════════════════════ */
export const newCond = (id) => ({kind: 'cond', id, target: null, op: null, value: null})
export const newGroup = (id, join = 'or') => ({kind: 'group', id, join, items: []})

/** 읽는 순서대로 조건에 번호를 매긴다 (그룹 안팎을 가로질러 1,2,3…). 번호를 돌려준다. */
export const numberConds = (root) => {
    let k = 0
    const walk = (g) => (g.items || []).forEach((it) => (it.kind === 'cond' ? (it.n = ++k) : walk(it)))
    walk(root)
    return k
}

export const allConds = (root, out = []) => {
    ;(root.items || []).forEach((it) => (it.kind === 'cond' ? out.push(it) : allConds(it, out)))
    return out
}

export const findParent = (root, node) => {
    for (const it of root.items || []) {
        if (it === node) return root
        if (it.kind === 'group') { const p = findParent(it, node); if (p) return p }
    }
    return null
}

/**
 * 조건 트리를 Python 으로. targets 는 targetsOf() 결과.
 * 돌려주는 것: {py, parts} — parts 는 사람 말을 조립할 재료(HTML 은 화면이 붙인다).
 *   leaf: {cond, target, op, valueText}
 *   group: {join, items, wrapped}
 */
export const buildFormula = (root, targets) => {
    const T = (id) => targets.find((t) => t.id === id)
    const leaf = (c) => {
        const t = c.target && T(c.target)
        if (!t) return null
        const ops = OPS[t.kind] || []
        const op = ops.find((o) => o.k === c.op) || ops[0]
        if (!op) return null
        let valueText = ''
        if (t.kind === 'choice' || t.kind === 'multi') {
            const ch = (t.choices || []).find((x) => x.value === c.value)
            valueText = ch ? ch.text : (c.value == null ? '' : String(c.value))
        } else if (t.kind === 'number') valueText = String(c.value == null ? '' : c.value)
        return {type: 'leaf', cond: c, target: t, op, valueText, py: op.py(t.path, c.value)}
    }
    const build = (node, isRoot) => {
        if (node.kind === 'cond') return leaf(node)
        const items = (node.items || []).map((it) => build(it, false)).filter(Boolean)
        if (!items.length) return null
        if (items.length === 1) return items[0]
        const py = items.map((p) => p.py).join(` ${node.join} `)
        return {type: 'group', join: node.join, items, wrapped: !isRoot, py: isRoot ? py : `(${py})`}
    }
    const r = build(root, true)
    return r ? {py: r.py, tree: r} : null
}

/* ══════════════════════════════════════════════════
 * 4. 색 — 순번 n 에서 계산. 표는 없다.
 *    황금각(137.508°)으로 색상환을 돌면 n 이 몇이든 이웃 순번끼리 멀리 떨어진다.
 *    oklch 는 색상이 바뀌어도 눈에 보이는 밝기가 같아 노랑·초록이 흐려지는 hsl 문제가 없다.
 * ══════════════════════════════════════════════════ */
export const hueOf = (n) => ((n - 1) * 137.508) % 360

export const colorOf = (n, oklch = true) => {
    const h = hueOf(n).toFixed(1)
    return oklch
        ? {c: `oklch(0.52 0.16 ${h})`, ct: `oklch(0.95 0.035 ${h})`}
        : {c: `hsl(${h} 62% 40%)`, ct: `hsl(${h} 70% 93%)`}
}

/* ══════════════════════════════════════════════════
 * 5. 읽기: 저장된 Python 문자열 → 사람 말 AST
 *    아는 모양만 바꾼다. 모르면 {ok:false} — 화면은 원문 그대로 + 배지.
 * ══════════════════════════════════════════════════ */
const NS = {'>=': '이상', '<=': '이하', '>': '초과', '<': '미만', '==': ''}
const EL = String.raw`structure\[(\d+)\](?:\.element_contents\[(\d+)\]|\['element_contents'\]\[(\d+)\])`   // m[1]=선택지, m[2]|m[3]=요소
const idx = (a, b) => (a !== undefined ? a : b)

const RULES = [
    [/^\s*result\s*(==|!=)\s*(['"])(.*?)\2\s*$/, (m) => `답이 「${m[3]}」${m[1] === '==' ? '이면' : '이 아니면'}`],
    [/^\s*(['"])(.*?)\1\s*(==|!=)\s*result\s*$/, (m) => `답이 「${m[2]}」${m[3] === '==' ? '이면' : '이 아니면'}`],
    [/^\s*(['"])(.*?)\1\s+in\s+result\s*$/, (m) => `「${m[2]}」를 골랐으면`],
    [/^\s*(['"])(.*?)\1\s+not\s+in\s+result\s*$/, (m) => `「${m[2]}」를 안 골랐으면`],
    [new RegExp(String.raw`^\s*(['"])(.*?)\1\s+in\s+${EL}(?:\.result|\['result'\])\s*$`), (m) => `${+m[3] + 1}번 선택지 안에서 「${m[2]}」를 골랐으면`],
    [new RegExp(String.raw`^\s*len\(\s*${EL}(?:\.result|\['result'\])\s*\)\s*>\s*0\s*$`), (m) => `${+m[1] + 1}번 선택지 안에서 하나라도 골랐으면`],
    [new RegExp(String.raw`^\s*(?:float|int)?\(?\s*${EL}\['result'\]\s*\)?\s*(>=|<=|>|<|==)\s*([0-9.]+)\s*$`), (m) => `${+m[1] + 1}번 선택지 안의 숫자가 ${m[5]} ${NS[m[4]]}이면`],
    [/^\s*result\s*(>=|<=|>|<|==)\s*([0-9.]+)\s*$/, (m) => `답이 ${m[2]} ${NS[m[1]]}이면`],
    [/^\s*result\s+in\s+\[(.*)\]\s*$/, (m) => `답이 ${m[1].split(',').map((s) => '「' + s.trim().replace(/^['"]|['"]$/g, '') + '」').join(' 또는 ')} 이면`],
    [/^\s*(?:len\(result\)\s*>\s*0|result\[0\]\[['"](?:filename|file_link)['"]\]\s*!=\s*['"]{2})\s*$/, () => `파일이 첨부되어 있으면`],
    [/^\s*(?:int|float)?\(?\s*structure\[(\d+)\]\[['"]([^'"]+)['"]\]\s*\)?\s*(>=|<=|>|<|==)\s*([0-9.]+)\s*$/, (m) => `표 ${+m[1] + 1}행 「${m[2]}」이 ${m[4]} ${NS[m[3]]}이면`],
    [/^\s*structure\[(\d+)\]\[['"]([^'"]+)['"]\]\s*(==|!=)\s*(['"])(.*?)\4\s*$/, (m) => `표 ${+m[1] + 1}행 「${m[2]}」이 「${m[5]}」${m[3] === '==' ? '이면' : '이 아니면'}`],
    [new RegExp(String.raw`^\s*(?:int|float)?\(?\s*${EL}\['structure'\]\[(\d+)\]\[['"]([^'"]+)['"]\]\s*\)?\s*(>=|<=|>|<|==)\s*([0-9.]+)\s*$`), (m) => `${+m[1] + 1}번 선택지 안 표 ${+m[4] + 1}행 「${m[5]}」이 ${m[7]} ${NS[m[6]]}이면`],
    [new RegExp(String.raw`^\s*${EL}\['structure'\]\[(\d+)\]\[['"]([^'"]+)['"]\]\s*(==|!=)\s*(['"])(.*?)\7\s*$`), (m) => `${+m[1] + 1}번 선택지 안 표 ${+m[4] + 1}행 「${m[5]}」이 「${m[8]}」${m[6] === '==' ? '이면' : '이 아니면'}`]
]
void idx

/** 깊이 0(괄호·문자열 밖)의 ' and ' / ' or ' 에서만 자른다 */
const splitTop = (s, word) => {
    const out = []
    let depth = 0, q = null, last = 0
    for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (q) { if (ch === q) q = null; continue }
        if (ch === "'" || ch === '"') { q = ch; continue }
        if (ch === '(' || ch === '[') depth++
        else if (ch === ')' || ch === ']') depth--
        else if (depth === 0 && s.startsWith(' ' + word + ' ', i)) { out.push(s.slice(last, i)); last = i + word.length + 2; i = last - 1 }
    }
    out.push(s.slice(last))
    return out
}

/** 전체를 감싼 괄호를 벗긴다. 함수 호출 괄호는 첫 글자가 '(' 가 아니라 안 걸린다. */
const unwrap = (src) => {
    const s = src.trim()
    if (s[0] !== '(') return {s, wrapped: false}
    let depth = 0, q = null
    for (let i = 0; i < s.length; i++) {
        const ch = s[i]
        if (q) { if (ch === q) q = null; continue }
        if (ch === "'" || ch === '"') { q = ch; continue }
        if (ch === '(') depth++
        else if (ch === ')') { depth--; if (depth === 0) return i === s.length - 1 ? {s: s.slice(1, -1), wrapped: true} : {s, wrapped: false} }
    }
    return {s, wrapped: false}
}

/**
 * 문자열 → AST. {ok:true, node} 또는 {ok:false, reason}
 *   node: {type:'leaf', say} | {type:'group', join, items, wrapped}
 */
export const readFormula = (src, top = true) => {
    if (typeof src !== 'string' || !src.trim()) return {ok: false, reason: 'empty'}
    const u = unwrap(src)
    const s = u.s
    // or 가 and 보다 느슨하다 — Python 우선순위 그대로. 괄호 없이 섞인 건 실데이터에 0건.
    for (const word of ['or', 'and']) {
        const parts = splitTop(s, word)
        if (parts.length > 1) {
            const items = parts.map((p) => readFormula(p, false))
            if (items.some((k) => !k.ok)) return {ok: false, reason: 'unknown-part'}
            return {ok: true, node: {type: 'group', join: word, items: items.map((k) => k.node), wrapped: !(top && !u.wrapped)}}
        }
    }
    for (const [re, say] of RULES) {
        const m = s.match(re)
        if (m) return {ok: true, node: {type: 'leaf', say: say(m)}}
    }
    return {ok: false, reason: classify(s)}
}

/** 못 읽은 이유를 세 가지로만 나눈다 — 화면 배지용 */
const classify = (s) => {
    if (/^\s*(['"]).*\1\s*$/.test(s)) return 'always-true'        // 문자열만 → Python 에서 항상 참
    if (!/result|structure/.test(s)) return 'not-a-formula'        // 답을 참조하지 않음
    return 'manual'                                                // 블럭으로 못 그리는 모양
}

/** AST → 평문 문장 (HTML 없음). 그룹은 괄호 대신 「 」로 감싼다. */
export const sentenceOf = (node) => {
    if (!node) return ''
    if (node.type === 'leaf') return node.say
    const inner = node.items.map(sentenceOf).join(node.join === 'and' ? ' 그리고 ' : ' 또는 ')
    return node.wrapped ? `[${inner}]` : inner
}

/* ══════════════════════════════════════════════════
 * 6. 증빙 위치(ref_path) — 이 기준의 파일이 붙어 있어야 하는 자리
 *    수식 대상과는 다른 축이다. 백엔드는 jmespath 점 표기로 저장하고
 *    (subs_init_evaluations.ref_path), 채점기가 attachment_position='opt' 일 때
 *    sub_path == ref_path 인 파일 개수를 세어 0이면 미충족으로 뒤집는다.
 *    첨부가 붙는 자리: 선택지(structure[i]) · 선택지 안 요소(.element_contents[j]) · 표 행(.structure[k])
 * ══════════════════════════════════════════════════ */
export const refPathsOf = (root) => {
    const out = []
    if (!root) return out
    const push = (node, path, say) => out.push({id: node.id, path, say})
    const t = root.subType || root.type
    if (t === 'table') {
        ;(root.children || []).forEach((row, k) => push(row, `structure[${k}]`, `표 ${k + 1}행`))
        return out
    }
    if (!CHOICE_PARENTS.includes(t)) return out
    ;(root.children || []).forEach((choice, i) => {
        const cSay = `${i + 1}번 선택지`
        const lbl = choice.label || ''
        push(choice, `structure[${i}]`, `${cSay} 「${lbl.slice(0, 18)}${lbl.length > 18 ? '…' : ''}」`)
        ;(choice.children || []).forEach((el, j) => {
            const base = `structure[${i}].element_contents[${j}]`
            push(el, base, `${cSay} 안 ${el.type === 'table' ? '표' : el.type}`)
            if (el.type === 'table') {
                ;(el.children || []).forEach((row, k) => push(row, `${base}.structure[${k}]`, `${cSay} 안 표 ${k + 1}행`))
            }
        })
    })
    return out
}

/* ══════════════════════════════════════════════════
 * 7. 기준(criterion) — df_evaluations_norm 한 행 + 라벨(df_evaluations_name) + 연결(ref_path, order)
 *    저장 계약은 백엔드 SubEvaluationHandler 의 ev_columns + evn_columns + s2e_columns 그대로다.
 * ══════════════════════════════════════════════════ */
export const IMPORTANCE = ['HIGH', 'MEDIUM', 'LOW']

/** 백엔드 /api/evaluation/codes 가 없을 때의 기본 수준표 (ev_code 테이블 실측, 2026-09-08) */
export const EV_CODES_DEFAULT = [
    {code: 'A', level: 100, description: '우수'},
    {code: 'B', level: 50, description: '보통'},
    {code: 'C', level: 25, description: '미흡'},
    {code: 'D', level: 10, description: '매우 미흡'},
    {code: 'U', level: 10, description: '모름'},
    {code: 'N', level: 0, description: '해당 없음'}
]

/**
 * 기준 이름표(df_evaluations_name)에 붙는 부가 항목. 백엔드 evn_columns 에 이미 있어 그대로 저장된다.
 * 전에는 _src 로 되돌려 보내기만 해서, 편집도 못 하고 새 기준에는 아예 실리지 않았다.
 */
export const CRITERION_EXTRA_KEYS = [
    'name_ea', 'name_ca', 'reverse_name_ea', 'reverse_name_ca',
    'ap_description', 'ap_description_ea', 'ap_description_ca',
    'is_ap', 'is_compliance', 'risk_lv', 'kesg_id'
]

/** 리스크 등급 — 실데이터 값에 맞춘다. v1 사전은 낮음을 'log' 로 쓰지만 그 값은 DB 에 0건이다. */
export const RISK_LEVELS = [
    {v: null, ko: '(미지정)'},
    {v: 'high', ko: '높음'},
    {v: 'medium', ko: '중간'},
    {v: 'low', ko: '낮음'}
]

export const newCriterion = (id) => ({
    id, ev_id: null, order: 0,
    name: '', reverse_name: '',
    formula: '', cond: newGroup(`${id}g`, 'and'), raw: false,
    score: 1, importance: 'MEDIUM', pos_ev_code: null, neg_ev_code: null, ignore_attach: false,
    ref_path: null, refNodeId: null,
    name_ea: '', name_ca: '', reverse_name_ea: '', reverse_name_ca: '',
    ap_description: '', ap_description_ea: '', ap_description_ca: '',
    is_ap: false, is_compliance: false, risk_lv: null, kesg_id: ''
})

/* ══════════════════════════════════════════════════
 * 8. 역방향: 저장된 수식 → 조건 트리
 *    블럭이 만든 수식은 표기가 고정돼 있어(OPS 의 py) 정확히 되돌릴 수 있다.
 *    손으로 쓴 수식까지 되돌리려는 게 아니다 — 그건 readFormula 로 '읽기'만 한다.
 *    규칙: 잎 하나라도 못 되돌리면 전체를 포기한다(반쯤 변환된 블럭은 더 헷갈린다).
 *    구조가 바뀌어(열 이름 변경 등) 주소가 안 맞아도 포기한다 — 문장으로는 계속 읽힌다.
 * ══════════════════════════════════════════════════ */
const rx = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const LEAF_FORMS = [
    // kind, 정규식 생성기(path 이스케이프본) → [op key, value 그룹 인덱스]
    {kind: 'choice', re: (P) => new RegExp(`^${P}(==|!=)'(.*)'$`),                     op: (m) => m[1], val: (m) => m[2]},
    {kind: 'multi',  re: (P) => new RegExp(`^'(.*)' (not in|in) ${P}$`),              op: (m) => m[2], val: (m) => m[1]},
    {kind: 'number', re: (P) => new RegExp(`^float\\(${P}\\) (>=|>|<=|<|==) (-?[0-9.]+)$`), op: (m) => m[1], val: (m) => Number(m[2])},
    {kind: 'text',   re: (P) => new RegExp(`^${P} (!=|==) ''$`),                       op: (m) => (m[1] === '!=' ? '!=""' : '==""'), val: () => null},
    {kind: 'file',   re: (P) => new RegExp(`^${P}\\[0\\]\\['filename'\\] !=''$`),   op: () => 'has', val: () => null}
]

const leafToCond = (src, targets, nextId) => {
    const s = src.trim()
    for (const t of targets) {
        const P = rx(t.path)
        for (const f of LEAF_FORMS) {
            if (f.kind !== t.kind) continue
            const m = s.match(f.re(P))
            if (!m) continue
            const ops = OPS[t.kind] || []
            const op = f.op(m)
            if (!ops.some((o) => o.k === op)) return null
            const c = newCond(nextId()); c.target = t.id; c.op = op; c.value = f.val(m)
            return c
        }
    }
    return null
}

/**
 * 수식 문자열 → 조건 트리. {ok:true, cond} 또는 {ok:false, reason}
 *   reason: 'empty' | 'unknown-leaf'(블럭 표기가 아니거나 구조와 안 맞음)
 */
export const formulaToCond = (formula, targets, idPrefix = 'r') => {
    let k = 0
    const nextId = () => `${idPrefix}${++k}`
    const parse = (src, join) => {
        const u = unwrap(src)
        const s = u.s
        for (const word of ['or', 'and']) {
            const parts = splitTop(s, word)
            if (parts.length > 1) {
                const g = newGroup(nextId(), word)
                for (const p of parts) { const child = parse(p, word); if (!child) return null; g.items.push(child) }
                return g
            }
        }
        return leafToCond(s, targets, nextId)
    }
    if (typeof formula !== 'string' || !formula.trim()) return {ok: false, reason: 'empty'}
    const node = parse(formula)
    if (!node) return {ok: false, reason: 'unknown-leaf'}
    // 루트는 언제나 그룹이어야 편집기가 다룬다
    if (node.kind === 'cond') { const g = newGroup(nextId(), 'and'); g.items.push(node); return {ok: true, cond: g} }
    return {ok: true, cond: node}
}

/**
 * API 에서 온 기준 → 편집 모델.
 * 블럭 표기로 저장된 수식은 블럭으로 되돌리고(raw:false), 그 밖은 문자열 그대로 둔다(raw:true).
 * 증빙 위치는 노드로 되찾는다.
 */
export const criterionFromApi = (ev, id, refPaths = [], targets = []) => {
    const hit = ev.ref_path ? refPaths.find((r) => r.path === ev.ref_path) : null
    const back = targets.length ? formulaToCond(ev.formula, targets, `${id}k`) : {ok: false}
    return {
        id, ev_id: ev.ev_id != null ? ev.ev_id : null, order: ev.order || 0,
        name: ev.name || '', reverse_name: ev.reverse_name || '',
        formula: ev.formula || '', cond: back.ok ? back.cond : null, raw: !back.ok,
        score: ev.score == null ? null : ev.score,
        importance: ev.importance || 'MEDIUM',
        pos_ev_code: ev.pos_ev_code || null, neg_ev_code: ev.neg_ev_code || null,
        ignore_attach: !!ev.ignore_attach,
        ref_path: ev.ref_path || null, refNodeId: hit ? hit.id : null,
        // 부가 항목 — 전에는 _src 로만 돌려보내 편집이 안 됐다. null 은 빈 값으로 펴서 입력에 바로 물린다.
        name_ea: ev.name_ea || '', name_ca: ev.name_ca || '',
        reverse_name_ea: ev.reverse_name_ea || '', reverse_name_ca: ev.reverse_name_ca || '',
        ap_description: ev.ap_description || '', ap_description_ea: ev.ap_description_ea || '',
        ap_description_ca: ev.ap_description_ca || '',
        is_ap: !!ev.is_ap, is_compliance: !!ev.is_compliance,
        risk_lv: ev.risk_lv || null, kesg_id: ev.kesg_id || '',
        _src: ev
    }
}

/** 편집 모델 → 저장 payload 항목. raw 면 문자열 그대로, 블럭이면 생성한 수식. */
export const criterionToApi = (c, targets, order) => {
    const built = !c.raw && c.cond ? buildFormula(c.cond, targets) : null
    const out = {
        ...(c.ev_id != null ? {ev_id: c.ev_id} : {}),
        order,
        formula: c.raw ? c.formula : (built ? built.py : ''),
        name: c.name || null, reverse_name: c.reverse_name || null,
        score: c.score == null || c.score === '' ? null : Number(c.score),
        importance: c.importance || 'MEDIUM',
        pos_ev_code: c.pos_ev_code || null, neg_ev_code: c.neg_ev_code || null,
        ignore_attach: !!c.ignore_attach,
        ref_path: c.ref_path || null
    }
    // 부가 항목 — 편집기가 값을 들고 있으면 그 값을, 아니면 원본을 그대로 돌려보낸다.
    // 새로 만든 기준도 이제 값이 실린다(전에는 _src 가 없어 통째로 빠졌다).
    const src = c._src || {}
    CRITERION_EXTRA_KEYS.forEach((k) => {
        if (c[k] !== undefined) { out[k] = c[k] === '' ? null : c[k]; return }
        if (src[k] !== undefined) out[k] = src[k]
    })
    return out
}
